import React, { useState } from 'react';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-hot-toast';
import DocxsAiLogo from '../shared/DocxsAiLogo';

const ForgotPasswordComponent = ({ onBack, onSuccess, isModal = false }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await apiRequest('/auth/forgot-password', {
        method: 'POST',
        body: { email },
      });

      if (response.success !== false) {
        setIsSuccess(true);
        toast.success('Password reset email sent! Check your inbox.');
        // Don't call onSuccess() here - let user see the success message and choose when to go back
      } else {
        toast.error(response.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      if (error.message.includes('User not found')) {
        toast.error('No account found with this email address');
      } else {
        toast.error('Failed to send reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={isModal ? "max-w-md mx-auto" : "text-center"}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-[var(--color-primary-soft)] mb-6">
            <svg className="h-8 w-8 text-[var(--color-success)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">
            Check your email
          </h2>
          <p className="text-sm text-[var(--color-text-muted)] mb-2">
            We've sent a password reset link to <strong className="text-[var(--color-text)]">{email}</strong>
          </p>
          <p className="text-sm text-[var(--color-text-muted)] mb-6">
            Didn't receive the email? Check your spam folder or try again.
          </p>
        </div>
        
        <div className="space-y-4">
          <button
            onClick={() => {
              setIsSuccess(false);
              setEmail('');
            }}
            className="w-full flex justify-center py-2.5 px-4 border border-[var(--color-border)] rounded-lg text-sm font-medium text-[var(--color-text)] bg-[var(--color-surface-muted)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-colors"
          >
            Try different email
          </button>
          
          <button
            onClick={onBack}
            className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium brand-gradient-btn focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? "max-w-md mx-auto" : ""}>
      <div className="text-center mb-6">
        {isModal && (
          <div className="flex justify-center mb-3 sm:mb-4">
            <DocxsAiLogo className="h-12 w-auto mx-auto" />
          </div>
        )}
        <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)] mb-4">
          Forgot your password?
        </h2>
        <p className="text-sm text-[var(--color-text-muted)]">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="themed-input w-full px-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] focus:border-[var(--color-primary)] sm:text-sm"
            placeholder="Enter your email address"
            disabled={isLoading}
          />
        </div>

        <div className="space-y-4">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium brand-gradient-btn disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[var(--color-text-inverse)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </div>
            ) : (
              'Send reset link'
            )}
          </button>
          
          <button
            type="button"
            onClick={onBack}
            className="w-full flex justify-center py-2.5 px-4 border border-[var(--color-border)] rounded-lg text-sm font-medium text-[var(--color-text)] bg-[var(--color-surface-muted)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-colors"
          >
            Back to login
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordComponent;
